import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Card, Form, Button, Table } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import API from '../services/api';
import ApprovalChip from './ApprovalChip';
import ExpenseCategoryPicker from './ExpenseCategoryPicker';
import ExpenseCategoriesModal from './ExpenseCategoriesModal';
import Pager from './Pager';
import { formatDay, todayDR, firstOfMonthDR } from '../utils/dates';
import Loading from './Loading';

const PAGE_SIZE = 15;

const emptyForm = {
  category: '', description: '', vendor: '', numeroComprobante: '', amount: '', date: todayDR(), method: 'efectivo', notes: '',
};

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
// An expense's date is a bare date stored as midnight UTC: read it as UTC, or Santo Domingo (UTC-4) shows the day before.
const dateStr = (d) => formatDay(d);

const firstOfMonth = firstOfMonthDR;
const today = todayDR; // the Dominican day (toISOString is UTC: tomorrow after 8 p.m.)
const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');

const GastosPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loaded, setLoaded] = useState(false); // the first load of the list has come back
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editingId, setEditingId] = useState(null);
  // The dashboard's Gastos card links here with ?categoria=&desde=&hasta= (a slice of its pie = that category, that month).
  const [searchParams, setSearchParams] = useSearchParams();
  const [from, setFrom] = useState(isDate(searchParams.get('desde')) ? searchParams.get('desde') : firstOfMonth());
  const [to, setTo] = useState(isDate(searchParams.get('hasta')) ? searchParams.get('hasta') : today());
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('categoria') || '');
  const [categories, setCategories] = useState([]);
  const [managingCategories, setManagingCategories] = useState(false);
  const [page, setPage] = useState(1);

  const loadExpenses = (category = categoryFilter, range = { from, to }) => {
    const { from, to } = range;
    API.get('/expenses', { params: { from, to, ...(category ? { category } : {}) } })
      .then(res => setExpenses(res.data))
      .catch(err => console.error('Error loading expenses:', err))
      .finally(() => setLoaded(true));
    setSearchParams({ desde: from, hasta: to, ...(category ? { categoria: category } : {}) }, { replace: true });
  };

  const loadCategories = () => {
    API.get('/expense-categories').then(res => setCategories(res.data)).catch(err => console.error('Error loading expense categories:', err));
  };

  useEffect(() => {
    loadExpenses();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Picking a date reloads the list by itself. Typing one passes through half-written dates (year 0002…), so it
  // waits for a pause and for a complete, sensible range.
  const rangeTimer = useRef(null);
  const changeRange = (next) => {
    if (next.from !== undefined) setFrom(next.from);
    if (next.to !== undefined) setTo(next.to);
    const range = { from, to, ...next };
    clearTimeout(rangeTimer.current);
    if (![range.from, range.to].every(d => /^20\d{2}-\d{2}-\d{2}$/.test(d)) || range.from > range.to) return;
    rangeTimer.current = setTimeout(() => { setPage(1); loadExpenses(categoryFilter, range); }, 400);
  };
  useEffect(() => () => clearTimeout(rangeTimer.current), []);

  const changeCategoryFilter = (name) => {
    setCategoryFilter(name);
    setPage(1);
    loadExpenses(name);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Editing loads the expense into the same form. The date is a bare date stored as midnight UTC, so its first ten
  // characters are the day itself.
  const startEdit = (expense) => {
    setEditingId(expense._id);
    setForm({
      category: expense.category || '',
      description: expense.description || '',
      vendor: expense.vendor || '',
      numeroComprobante: expense.numeroComprobante || '',
      amount: String(expense.amount ?? ''),
      date: expense.date ? String(expense.date).slice(0, 10) : today(),
      method: expense.method || 'efectivo',
      notes: expense.notes || '',
    });
    setError('');
    setNotice('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    try {
      const body = { ...form, amount: Number(form.amount) };
      if (editingId) {
        await API.put(`/expenses/${editingId}`, body);
        setNotice('Gasto actualizado.');
        setEditingId(null);
      } else {
        await API.post('/expenses', body);
      }
      setForm(emptyForm);
      loadExpenses();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar el gasto.');
    }
  };

  // The voucher sent with a gasto recorded by WhatsApp. The tab is opened right away (a pop-up opened after the
  // download would be blocked) and shown the file once it arrives.
  const openVoucher = async (id) => {
    const tab = window.open('', '_blank');
    try {
      const { data } = await API.get(`/expenses/${id}/voucher`, { responseType: 'blob' });
      if (tab) tab.location.href = URL.createObjectURL(data);
    } catch (err) {
      if (tab) tab.close();
      setError('No se pudo abrir el comprobante.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    setError('');
    try {
      await API.delete(`/expenses/${id}`);
      if (id === editingId) cancelEdit();
      loadExpenses();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo eliminar el gasto.');
    }
  };

  const total = expenses.reduce((sum, e) => sum + e.amount, 0);
  // The total above is the whole range's, not the page's. Editing or deleting keeps the page (clamped if it emptied).
  const pages = Math.max(1, Math.ceil(expenses.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = expenses.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  // Like Manifold Manager: nothing but the loading indicator until the page's data has come back.
  if (!loaded) return <Loading page label="Cargando gastos..." />;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="app-title mb-0" style={{ textAlign: 'left' }}>Gastos</h2>
        <Button variant="outline-secondary" size="sm" onClick={() => setManagingCategories(true)}><i className="bi bi-tags me-1" />Categorías</Button>
      </div>
      {managingCategories && (
        <ExpenseCategoriesModal
          categories={categories}
          onClose={() => setManagingCategories(false)}
          // A rename, join or delete changes gastos' category: reload both, and drop a filter on a category that's gone.
          // Adding one changes no gasto.
          onChanged={(kind) => { loadCategories(); if (kind !== 'add') changeCategoryFilter(''); }}
        />
      )}
      <div className="mb-4" />

      <Row>
        <Col md={4}>
          <Card className="shadow-sm p-3" style={editingId ? { borderColor: 'var(--mm-cyan)' } : undefined}>
            <Card.Title>{editingId ? 'Editar Gasto' : 'Nuevo Gasto'}</Card.Title>
            {error && <div className="alert alert-danger">{error}</div>}
            {notice && <div className="alert alert-info">{notice}</div>}
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-2">
                <Form.Label>Categoría</Form.Label>
                <ExpenseCategoryPicker
                  categories={categories}
                  value={form.category}
                  onChange={(name) => setForm(prev => ({ ...prev, category: name }))}
                  onCreated={loadCategories}
                  allowCreate
                  required
                  placeholder="Escribe para buscar o agregar..."
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Descripción</Form.Label>
                <Form.Control name="description" value={form.description} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Proveedor</Form.Label>
                <Form.Control name="vendor" value={form.vendor} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>No. de Comprobante</Form.Label>
                <Form.Control name="numeroComprobante" value={form.numeroComprobante} onChange={handleChange} placeholder="NCF o número de recibo del proveedor" />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Monto (DOP)</Form.Label>
                <Form.Control type="number" step="0.01" name="amount" value={form.amount} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Fecha</Form.Label>
                <Form.Control type="date" name="date" value={form.date} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Método de Pago</Form.Label>
                <Form.Select name="method" value={form.method} onChange={handleChange} className="form-control">
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="cheque">Cheque</option>
                </Form.Select>
              </Form.Group>
              <Button type="submit" className="w-100">{editingId ? 'Guardar Cambios' : 'Guardar Gasto'}</Button>
              {editingId && <Button variant="secondary" className="w-100 mt-2" onClick={cancelEdit}>Cancelar</Button>}
            </Form>
          </Card>
        </Col>
        <Col md={8}>
          <Card className="shadow-sm p-3 mb-3">
            <Row className="align-items-end">
              <Col md={3}>
                <Form.Label>Desde</Form.Label>
                <Form.Control type="date" value={from} onChange={e => changeRange({ from: e.target.value })} />
              </Col>
              <Col md={3}>
                <Form.Label>Hasta</Form.Label>
                <Form.Control type="date" value={to} onChange={e => changeRange({ to: e.target.value })} />
              </Col>
              <Col md={3}>
                <Button variant="outline-primary" onClick={() => loadExpenses()}>Actualizar</Button>
              </Col>
              <Col md={3}>
                <Form.Label>Categoría</Form.Label>
                <ExpenseCategoryPicker categories={categories} value={categoryFilter} onChange={changeCategoryFilter} clearLabel="Todas las categorías" />
              </Col>
            </Row>
          </Card>
          <Card className="shadow-sm p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Card.Title className="mb-0">Registro de Gastos{categoryFilter && <span className="text-muted" style={{ fontWeight: 400 }}> — {categoryFilter}</span>}</Card.Title>
              <div style={{ fontWeight: 700, color: 'var(--mm-cyan)' }}>{money(total)}</div>
            </div>
            <Table hover responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Fecha</th><th>Categoría</th><th>Descripción</th><th>Proveedor</th><th>No. Comprobante</th>
                  <th className="text-end">Monto</th><th>Método</th><th></th>
                </tr>
              </thead>
              <tbody>
                {slice.map(e => (
                  <tr key={e._id} className={e._id === editingId ? 'table-active' : undefined}>
                    <td>{dateStr(e.date)}</td>
                    <td>{e.category}</td>
                    <td>
                      {e.description || '—'}
                      {e.voucher && <Button size="sm" variant="link" className="p-0 ms-1 align-baseline" title="Ver comprobante" onClick={() => openVoucher(e._id)}>📎</Button>}
                      <ApprovalChip entityId={e._id} className="ms-1" />
                    </td>
                    <td>{e.vendor || '—'}</td>
                    <td>{e.numeroComprobante || '—'}</td>
                    <td className="text-end">{money(e.amount)}</td>
                    <td>{e.method}</td>
                    <td className="text-end text-nowrap">
                      <Button size="sm" variant="outline-primary" className="me-1" onClick={() => startEdit(e)}>Editar</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(e._id)}>Eliminar</Button>
                    </td>
                  </tr>
                ))}
                {expenses.length === 0 && (
                  <tr><td colSpan={8} className="text-muted text-center">{!loaded ? <Loading label="Cargando gastos..." /> : categoryFilter ? `Sin gastos de "${categoryFilter}" en este rango.` : 'Sin gastos en este rango.'}</td></tr>
                )}
              </tbody>
            </Table>
            {pages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="text-muted small">Página {current} de {pages} — {expenses.length} gastos</span>
                <Pager page={current} pages={pages} onChange={setPage} />
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default GastosPage;
